import React, { useEffect, useMemo, useRef, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import api from "../lib/api";
import { CheckCircle2, XCircle, Trash2, Calendar, Users, Phone, Mail, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";

export default function AdminReservations() {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState({ date: "", status: "" });
    const highlightId = useMemo(() => {
        if (typeof window === "undefined") return null;
        return new URLSearchParams(window.location.search).get("highlight");
    }, []);
    const highlightRef = useRef(null);

    const load = async () => {
        const params = {};
        if (filter.date) params.date_filter = filter.date;
        if (filter.status) params.status = filter.status;
        try {
            const r = await api.get("/admin/reservations", { params });
            setList(r.data);
        } catch { toast.error("Errore caricamento"); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.date, filter.status]);

    // Auto-scroll & flash the reservation linked from the owner email CTA.
    useEffect(() => {
        if (!highlightId || !list.length || !highlightRef.current) return;
        highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [highlightId, list.length]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/reservations/${id}`, { status });
            toast.success(status === "confirmed" ? "Prenotazione confermata" : "Prenotazione annullata");
            load();
        } catch { toast.error("Errore"); }
    };

    const setArrival = async (id, arrival_status) => {
        try {
            await api.patch(`/admin/reservations/${id}/arrival`, { arrival_status });
            toast.success(
                arrival_status === "arrived" ? "Cliente segnato arrivato" :
                arrival_status === "no_show" ? "Segnato come No-show" : "Reset arrivo"
            );
            load();
        } catch { toast.error("Errore aggiornamento arrivo"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare definitivamente?")) return;
        try {
            await api.delete(`/admin/reservations/${id}`);
            toast.success("Eliminata");
            load();
        } catch { toast.error("Errore"); }
    };

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-reservations-page">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="mb-8">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                    <h1 className="font-serif text-5xl text-white mt-2">Prenotazioni</h1>
                </div>

                <div className="flex flex-wrap gap-4 mb-8">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold block mb-2">Data</label>
                        <input type="date" value={filter.date} onChange={(e) => setFilter({ ...filter, date: e.target.value })}
                            data-testid="filter-date-input"
                            className="px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold block mb-2">Stato</label>
                        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                            data-testid="filter-status-input"
                            className="px-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none">
                            <option value="">Tutti</option>
                            <option value="pending">In attesa</option>
                            <option value="confirmed">Confermate</option>
                            <option value="cancelled">Annullate</option>
                        </select>
                    </div>
                    {(filter.date || filter.status) && (
                        <button onClick={() => setFilter({ date: "", status: "" })} className="self-end px-4 py-2.5 text-xs text-gray-400 hover:text-gold uppercase tracking-widest">
                            Reset
                        </button>
                    )}
                </div>

                <div className="space-y-3">
                    {list.length === 0 ? (
                        <div className="bg-ink-surface border border-white/10 rounded-sm p-12 text-center text-gray-500">
                            Nessuna prenotazione trovata.
                        </div>
                    ) : list.map((r) => {
                        const isHighlighted = r.reservation_id === highlightId;
                        return (
                        <div key={r.reservation_id} data-testid={`admin-res-${r.reservation_id}`}
                            ref={isHighlighted ? highlightRef : null}
                            className={`bg-ink-surface border rounded-sm p-5 transition-all ${isHighlighted ? "border-gold ring-2 ring-gold/40 animate-pulse-once" : "border-white/10 hover:border-gold/30"}`}>
                            <div className="flex items-start justify-between flex-wrap gap-4">
                                <div className="space-y-2 flex-1">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-serif text-2xl text-white">{r.customer_name}</span>
                                        <StatusPill status={r.status} />
                                        <ArrivalPill arrival={r.arrival_status} />
                                        {r.is_guest && (
                                            <span className="text-[9px] uppercase tracking-widest border px-2 py-1 rounded-sm bg-violet-500/10 text-violet-300 border-violet-500/40 inline-flex items-center gap-1" data-testid={`guest-pill-${r.reservation_id}`} title="Prenotazione tramite Private Mirror (senza Google Login)">
                                                ✦ Mirror
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                                        <InfoDot icon={Calendar}>{r.date} · {r.slot_time}</InfoDot>
                                        <InfoDot icon={Users}>{r.party_size} coperti · {r.table_number ? `Tavolo ${r.table_number}` : "Tavolo da assegnare"}</InfoDot>
                                        <InfoDot icon={Phone}>{r.customer_phone}</InfoDot>
                                        <InfoDot icon={Mail}>{r.customer_email}</InfoDot>
                                    </div>
                                    {r.special_requests && <div className="text-xs text-gray-400 italic mt-2">"{r.special_requests}"</div>}
                                    {r.allergies && (
                                        <div className="mt-2 inline-flex items-start gap-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-sm px-2.5 py-1.5" data-testid={`allergies-${r.reservation_id}`}>
                                            <span className="font-semibold uppercase tracking-widest text-[10px] mt-0.5">Allergie:</span>
                                            <span>{r.allergies}</span>
                                        </div>
                                    )}
                                    {r.bringing_pet && (
                                        <div className="mt-2 ml-2 inline-flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2.5 py-1.5" data-testid={`pet-${r.reservation_id}`}>
                                            🐾 <span className="font-semibold uppercase tracking-widest text-[10px]">Porta animale</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {r.status !== "confirmed" && r.status !== "cancelled" && (
                                        <button onClick={() => setStatus(r.reservation_id, "confirmed")}
                                            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-sm text-xs uppercase tracking-widest hover:bg-emerald-500/20"
                                            data-testid={`confirm-${r.reservation_id}`}>
                                            <CheckCircle2 size={14} /> Conferma
                                        </button>
                                    )}
                                    {r.status !== "cancelled" && (
                                        <button onClick={() => setStatus(r.reservation_id, "cancelled")}
                                            className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-sm text-xs uppercase tracking-widest hover:bg-red-500/20"
                                            data-testid={`cancel-${r.reservation_id}`}>
                                            <XCircle size={14} /> Annulla
                                        </button>
                                    )}
                                    {r.status !== "cancelled" && r.arrival_status !== "arrived" && (
                                        <button onClick={() => setArrival(r.reservation_id, "arrived")}
                                            className="flex items-center gap-1.5 px-3 py-2 bg-gold/10 text-gold border border-gold/40 rounded-sm text-xs uppercase tracking-widest hover:bg-gold/20"
                                            data-testid={`arrived-${r.reservation_id}`}>
                                            <UserCheck size={14} /> Arrivato
                                        </button>
                                    )}
                                    {r.status !== "cancelled" && r.arrival_status !== "no_show" && r.arrival_status !== "arrived" && (
                                        <button onClick={() => setArrival(r.reservation_id, "no_show")}
                                            className="flex items-center gap-1.5 px-3 py-2 bg-gray-500/10 text-gray-300 border border-gray-500/30 rounded-sm text-xs uppercase tracking-widest hover:bg-gray-500/20"
                                            data-testid={`noshow-${r.reservation_id}`}>
                                            <UserX size={14} /> No-show
                                        </button>
                                    )}
                                    <button onClick={() => remove(r.reservation_id)}
                                        className="p-2 text-gray-500 hover:text-red-400"
                                        data-testid={`delete-${r.reservation_id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </main>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: { label: "In attesa", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
        confirmed: { label: "Confermata", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
        cancelled: { label: "Annullata", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
    };
    const s = map[status] || map.pending;
    return <span className={`text-[9px] uppercase tracking-widest border px-2 py-1 rounded-sm ${s.cls}`}>{s.label}</span>;
}

function ArrivalPill({ arrival }) {
    if (!arrival || arrival === "unknown") return null;
    const map = {
        arrived: { label: "Arrivato", cls: "bg-gold/10 text-gold border-gold/40" },
        no_show: { label: "No-show", cls: "bg-gray-500/10 text-gray-300 border-gray-500/30" },
    };
    const s = map[arrival];
    if (!s) return null;
    return <span className={`text-[9px] uppercase tracking-widest border px-2 py-1 rounded-sm ${s.cls}`}>{s.label}</span>;
}

function InfoDot({ icon: Icon, children }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <Icon size={12} strokeWidth={1.5} className="text-gray-500" />
            {children}
        </span>
    );
}
